import {
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  CalendarMonthRounded,
  RefreshRounded,
  ScheduleRounded,
  TodayRounded,
  WorkOutlineRounded,
} from '@mui/icons-material';

import ProaDatePicker
  from '../../../components/Common/ProaDatePicker';
import {
  getStacMyDedication,
} from '../services/stacService';


function toDateKey(
  value: Date,
): string {
  const year = value.getFullYear();
  const month = String(
    value.getMonth() + 1
  ).padStart(2, '0');
  const day = String(
    value.getDate()
  ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}


function parseDateKey(
  value: string,
): Date {
  const [
    year,
    month,
    day,
  ] = value.split(
    '-'
  ).map(Number);

  return new Date(
    year,
    month - 1,
    day,
  );
}


function addDays(
  value: string,
  days: number,
): string {
  const result = parseDateKey(
    value
  );

  result.setDate(
    result.getDate() + days
  );

  return toDateKey(
    result
  );
}


function getMonday(
  value: string,
): string {
  const date = parseDateKey(
    value
  );

  const weekday = date.getDay();

  const delta = weekday === 0
    ? -6
    : 1 - weekday;

  date.setDate(
    date.getDate() + delta
  );

  return toDateKey(
    date
  );
}


function formatDuration(
  seconds: number,
): string {
  const totalMinutes = Math.floor(
    Math.max(
      0,
      seconds
    ) / 60
  );

  const hours = Math.floor(
    totalMinutes / 60
  );

  const minutes =
    totalMinutes % 60;

  if (
    hours === 0
    && minutes === 0
  ) {
    return '0 min';
  }

  if (hours === 0) {
    return `${minutes} min`;
  }

  if (minutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${String(
    minutes
  ).padStart(2, '0')} min`;
}


function formatDayLabel(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      weekday: 'short',
    },
  )
    .format(
      parseDateKey(value)
    )
    .replace(
      '.',
      ''
    );
}


function formatDayNumber(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: '2-digit',
    },
  ).format(
    parseDateKey(value)
  );
}


function formatLongDate(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
  ).format(
    parseDateKey(value)
  );
}


export default function StacMyDedicationView() {
  const todayKey = useMemo(
    () => toDateKey(
      new Date()
    ),
    [],
  );

  const browserTimezone = useMemo(
    () => (
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone
      || 'UTC'
    ),
    [],
  );

  const [
    selectedDay,
    setSelectedDay,
  ] = useState(
    todayKey
  );

  const weekStart = getMonday(
    selectedDay
  );

  const weekEnd = addDays(
    weekStart,
    6
  );

  const currentWeekStart = getMonday(
    todayKey
  );

  const dedicationQuery = useQuery({
    queryKey: [
      'stac-dedication',
      weekStart,
      weekEnd,
      browserTimezone,
    ],
    queryFn: () =>
      getStacMyDedication({
        from: weekStart,
        to: weekEnd,
        timezone:
          browserTimezone,
      }),
    retry: false,
    refetchInterval: 60_000,
  });

  const dedication =
    dedicationQuery.data;

  const selectedDaySummary =
    dedication?.days.find(
      (day) =>
        day.date === selectedDay
    );

  const canGoNextWeek =
    weekStart < currentWeekStart;

  const weekLabel = `${formatDayNumber(
    weekStart
  )} — ${formatDayNumber(
    weekEnd
  )}`;

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border:
            '1px solid rgba(148,163,184,0.16)',
          bgcolor:
            'rgba(15,23,42,0.72)',
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              md: 'center',
            }}
            spacing={1.5}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <ScheduleRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    fontSize: '1.05rem',
                  }}
                >
                  Mi dedicación
                </Typography>
              </Stack>

              <Typography
                sx={{
                  mt: 0.4,
                  color: '#94a3b8',
                  fontSize: '0.86rem',
                }}
              >
                Consulta únicamente el tiempo
                que has registrado.
              </Typography>
            </Box>

            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1}
              alignItems="center"
            >
              <Button
                variant="outlined"
                startIcon={
                  <ArrowBackRounded />
                }
                onClick={() =>
                  setSelectedDay(
                    addDays(
                      selectedDay,
                      -7,
                    )
                  )
                }
                sx={{
                  textTransform: 'none',
                }}
              >
                Semana anterior
              </Button>

              <Button
                variant="outlined"
                startIcon={
                  <TodayRounded />
                }
                disabled={
                  selectedDay === todayKey
                }
                onClick={() =>
                  setSelectedDay(
                    todayKey
                  )
                }
                sx={{
                  textTransform: 'none',
                }}
              >
                Hoy
              </Button>

              <Button
                variant="outlined"
                endIcon={
                  <ArrowForwardRounded />
                }
                disabled={
                  !canGoNextWeek
                }
                onClick={() =>
                  setSelectedDay(
                    addDays(
                      selectedDay,
                      7,
                    )
                  )
                }
                sx={{
                  textTransform: 'none',
                }}
              >
                Semana siguiente
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md:
                  'minmax(220px, 0.45fr) '
                  + 'minmax(0, 1fr)',
              },
              gap: 1.5,
            }}
          >
            <ProaDatePicker
              label="Día seleccionado"
              value={selectedDay}
              onChange={(value) => {
                if (
                  value
                  && value <= todayKey
                ) {
                  setSelectedDay(
                    value
                  );
                }
              }}
              maxDate={todayKey}
            />

            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border:
                  '1px solid rgba(56,189,248,0.18)',
                bgcolor:
                  'rgba(2,6,23,0.32)',
                px: 2,
                py: 1.35,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontWeight: 800,
                }}
              >
                Semana
              </Typography>

              <Typography
                sx={{
                  mt: 0.2,
                  color: '#e2e8f0',
                  fontWeight: 850,
                }}
              >
                {weekLabel}
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </Paper>

      {dedicationQuery.isLoading ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.14)',
            bgcolor:
              'rgba(15,23,42,0.72)',
            p: 2.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <CircularProgress
              size={20}
            />

            <Typography
              sx={{
                color: '#94a3b8',
              }}
            >
              Cargando dedicación...
            </Typography>
          </Stack>
        </Paper>
      ) : dedicationQuery.isError ? (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={
                <RefreshRounded />
              }
              onClick={() => {
                void dedicationQuery.refetch();
              }}
            >
              Reintentar
            </Button>
          }
        >
          No se pudo cargar tu dedicación.
        </Alert>
      ) : dedication ? (
        <>
          {dedication.has_overlaps ? (
            <Alert
              severity="warning"
              variant="outlined"
            >
              Hay franjas solapadas dentro
              del período. Los totales reflejan
              los registros existentes hasta que
              se corrijan esos solapamientos.
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.14)',
              bgcolor:
                'rgba(15,23,42,0.72)',
              p: 2.5,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Semana
                </Typography>

                <Typography
                  sx={{
                    mt: 0.25,
                    color: '#94a3b8',
                    fontSize: '0.85rem',
                  }}
                >
                  Tiempo registrado:
                  {' '}
                  <Box
                    component="span"
                    sx={{
                      color: '#e2e8f0',
                      fontWeight: 900,
                    }}
                  >
                    {formatDuration(
                      dedication.total_seconds
                    )}
                  </Box>
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs:
                      'repeat(2, minmax(0, 1fr))',
                    sm:
                      'repeat(4, minmax(0, 1fr))',
                    lg:
                      'repeat(7, minmax(0, 1fr))',
                  },
                  gap: 1,
                }}
              >
                {dedication.days.map(
                  (day) => {
                    const isSelected =
                      day.date
                      === selectedDay;

                    const isFuture =
                      day.date
                      > todayKey;

                    return (
                      <Button
                        key={day.date}
                        variant={
                          isSelected
                            ? 'contained'
                            : 'outlined'
                        }
                        disabled={
                          isFuture
                        }
                        onClick={() =>
                          setSelectedDay(
                            day.date
                          )
                        }
                        sx={{
                          minHeight: 88,
                          px: 1,
                          textTransform:
                            'none',
                          borderRadius: 2.5,
                        }}
                      >
                        <Stack
                          spacing={0.25}
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              fontSize:
                                '0.72rem',
                              fontWeight: 900,
                              textTransform:
                                'uppercase',
                            }}
                          >
                            {formatDayLabel(
                              day.date
                            )}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize:
                                '0.82rem',
                            }}
                          >
                            {formatDayNumber(
                              day.date
                            )}
                          </Typography>

                          <Typography
                            sx={{
                              mt:
                                '4px !important',
                              fontSize:
                                '0.8rem',
                              fontWeight: 900,
                            }}
                          >
                            {formatDuration(
                              day.total_seconds
                            )}
                          </Typography>
                        </Stack>
                      </Button>
                    );
                  },
                )}
              </Box>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg:
                  'minmax(0, 0.8fr) '
                  + 'minmax(0, 1.2fr)',
              },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <CalendarMonthRounded
                    sx={{
                      color: '#38bdf8',
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Día seleccionado
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.78rem',
                        textTransform:
                          'capitalize',
                      }}
                    >
                      {formatLongDate(
                        selectedDay
                      )}
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '1.55rem',
                  }}
                >
                  {formatDuration(
                    selectedDaySummary
                      ?.total_seconds
                    ?? 0
                  )}
                </Typography>

                {selectedDaySummary
                  ?.tasks.length ? (
                  <Stack spacing={1}>
                    {selectedDaySummary
                      .tasks
                      .map((task) => (
                        <Paper
                          key={
                            task.task_id
                          }
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border:
                              '1px solid rgba(148,163,184,0.10)',
                            bgcolor:
                              'rgba(2,6,23,0.28)',
                            px: 1.4,
                            py: 1.1,
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Typography
                              sx={{
                                color:
                                  '#e2e8f0',
                                fontWeight:
                                  750,
                              }}
                            >
                              {
                                task.task_title
                              }
                            </Typography>

                            <Typography
                              sx={{
                                color:
                                  '#7dd3fc',
                                fontWeight:
                                  900,
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              {formatDuration(
                                task.total_seconds
                              )}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.88rem',
                    }}
                  >
                    No has registrado tiempo
                    en este día.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border:
                  '1px solid rgba(148,163,184,0.14)',
                bgcolor:
                  'rgba(15,23,42,0.72)',
                p: 2.5,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <WorkOutlineRounded
                    sx={{
                      color: '#38bdf8',
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Distribución semanal
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.78rem',
                      }}
                    >
                      Tiempo registrado por trabajo.
                    </Typography>
                  </Box>
                </Stack>

                {dedication.tasks.length ? (
                  <Stack spacing={1.4}>
                    {dedication.tasks.map(
                      (task) => {
                        const percentage =
                          dedication.total_seconds
                            > 0
                            ? (
                              task.total_seconds
                              / dedication.total_seconds
                            ) * 100
                            : 0;

                        return (
                          <Box
                            key={
                              task.task_id
                            }
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography
                                sx={{
                                  color:
                                    '#e2e8f0',
                                  fontWeight:
                                    800,
                                }}
                              >
                                {
                                  task.task_title
                                }
                              </Typography>

                              <Typography
                                sx={{
                                  color:
                                    '#94a3b8',
                                  fontWeight:
                                    850,
                                  whiteSpace:
                                    'nowrap',
                                }}
                              >
                                {formatDuration(
                                  task.total_seconds
                                )}
                              </Typography>
                            </Stack>

                            <LinearProgress
                              variant="determinate"
                              value={
                                Math.min(
                                  100,
                                  percentage
                                )
                              }
                              sx={{
                                mt: 0.75,
                                height: 7,
                                borderRadius: 99,
                                bgcolor:
                                  alpha(
                                    '#64748b',
                                    0.16,
                                  ),
                              }}
                            />
                          </Box>
                        );
                      },
                    )}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.88rem',
                    }}
                  >
                    Todavía no hay dedicación
                    registrada en esta semana.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}